
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SOCIAL_LINKS } from '../../lib/constants';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Container from '../ui/Container';
import Section from '../ui/Section';

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    project: '',
    budget: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const maxMessageLength = 500;

  // CAPTCHA states
  const [captcha, setCaptcha] = useState({
    num1: Math.floor(Math.random() * 10) + 1,
    num2: Math.floor(Math.random() * 10) + 1,
    answer: '',
    isValid: false
  });

  const generateNewCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptcha({
      num1,
      num2,
      answer: '',
      isValid: false
    });
  };

  const handleCaptchaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const answer = e.target.value;
    const isValid = parseInt(answer) === (captcha.num1 + captcha.num2);
    setCaptcha(prev => ({
      ...prev,
      answer,
      isValid
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate CAPTCHA before submitting
    if (!captcha.isValid) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const formUrl = 'https://hook.us1.make.com/a19vqp5q7jq4r7w5t88c5sxi13vdxgtx';
      
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('company', formData.company);
      formBody.append('project', formData.project);
      formBody.append('budget', formData.budget);
      formBody.append('message', formData.message);

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          project: '',
          budget: '',
          message: ''
        });
        // Generate new CAPTCHA after successful submission
        generateNewCaptcha();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > maxMessageLength) {
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const contactInfo = [
    {
      icon: 'ri-mail-line',
      label: t('contact.info.email'),
      value: 'hello@balamtech.com',
      href: 'mailto:hello@balamtech.com'
    },
    {
      icon: 'ri-phone-line',
      label: t('contact.info.phone'),
      value: '+1 (555) 123-4567',
      href: 'tel:+15551234567'
    },
    {
      icon: 'ri-time-line',
      label: t('contact.info.responseTime'),
      value: t('contact.info.responseTimeValue'),
      href: null
    }
  ];

  return (
    <Section id="contact" className="py-20 bg-gradient-to-b from-blue-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml,%3Csvg width=&quot;60&quot; height=&quot;60&quot; viewBox=&quot;0 0 60 60&quot; xmlns=&quot;http://www.w3.org/2000/svg&quot;%3E%3Cg fill=&quot;none&quot; fill-rule=&quot;evenodd&quot;%3E%3Cg fill=&quot;%232563eb&quot; fill-opacity=&quot;0.02&quot;%3E%3Ccircle cx=&quot;30&quot; cy=&quot;30&quot; r=&quot;2&quot;/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-50"></div>
      
      <Container className="relative z-10">
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4 font-poppins">
            {t('contact.title')}
          </h2>
          <p className="text-xl text-blue-700 max-w-3xl mx-auto font-inter">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          {/* Contact Form - Takes up 1 column */}
          <div className="w-full">
            <Card className="h-full animate-slide-up bg-gradient-to-b from-white to-blue-50/30 shadow-colored">
              <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-6 h-full flex flex-col">
                <div className="flex-1 space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-blue-900 mb-2 font-inter">
                        {t('contact.form.name')} <span className="text-red-500">{t('common.required')}</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder={t('contact.form.placeholders.name')}
                        className="w-full px-4 py-3 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 text-sm font-inter shadow-sm hover:shadow-md"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-blue-900 mb-2 font-inter">
                        {t('contact.form.email')} <span className="text-red-500">{t('common.required')}</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder={t('contact.form.placeholders.email')}
                        className="w-full px-4 py-3 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 text-sm font-inter shadow-sm hover:shadow-md"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-blue-900 mb-2 font-inter">
                      {t('contact.form.company')}
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder={t('contact.form.placeholders.company')}
                      className="w-full px-4 py-3 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 text-sm font-inter shadow-sm hover:shadow-md"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="project" className="block text-sm font-semibold text-blue-900 mb-2 font-inter">
                        {t('contact.form.project')} <span className="text-red-500">{t('common.required')}</span>
                      </label>
                      <div className="relative">
                        <select
                          id="project"
                          name="project"
                          required
                          value={formData.project}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 pr-8 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 text-sm font-inter appearance-none shadow-sm hover:shadow-md bg-white"
                        >
                          <option value="">{t('contact.form.projectTypes.select')}</option>
                          <option value="website">{t('contact.form.projectTypes.website')}</option>
                          <option value="landing">{t('contact.form.projectTypes.landing')}</option>
                          <option value="webapp">{t('contact.form.projectTypes.webapp')}</option>
                          <option value="api">{t('contact.form.projectTypes.api')}</option>
                          <option value="other">{t('contact.form.projectTypes.other')}</option>
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-600 pointer-events-none"></i>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="budget" className="block text-sm font-semibold text-blue-900 mb-2 font-inter">
                        {t('contact.form.budget')}
                      </label>
                      <div className="relative">
                        <select
                          id="budget"
                          name="budget"
                          value={formData.budget}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 pr-8 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 text-sm font-inter appearance-none shadow-sm hover:shadow-md bg-white"
                        >
                          <option value="">{t('contact.form.budgetRanges.select')}</option>
                          <option value="5k-10k">{t('contact.form.budgetRanges.5k10k')}</option>
                          <option value="10k-25k">{t('contact.form.budgetRanges.10k25k')}</option>
                          <option value="25k-50k">{t('contact.form.budgetRanges.25k50k')}</option>
                          <option value="50k+">{t('contact.form.budgetRanges.50kplus')}</option>
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-600 pointer-events-none"></i>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-blue-900 mb-2 font-inter">
                      {t('contact.form.message')} <span className="text-red-500">{t('common.required')}</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      required
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder={t('contact.form.placeholders.message')}
                      maxLength={maxMessageLength}
                      className="w-full px-4 py-3 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 text-sm font-inter resize-vertical shadow-sm hover:shadow-md"
                    />
                    <div className="text-right mt-1">
                      <span className="text-xs text-blue-600 font-inter">
                        {maxMessageLength - formData.message.length} {t('contact.form.charactersLeft')}
                      </span>
                    </div>
                  </div>

                  {/* CAPTCHA Section */}
                  <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-200">
                    <label htmlFor="captcha" className="block text-sm font-semibold text-blue-900 mb-3 font-inter">
                      {t('contact.form.captcha.title')} <span className="text-red-500">{t('common.required')}</span>
                    </label>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2 bg-white px-4 py-3 rounded-lg border border-blue-200 shadow-sm">
                        <span className="text-lg font-bold text-blue-900 font-mono">
                          {captcha.num1} + {captcha.num2} =
                        </span>
                        <i className="ri-question-mark text-blue-600"></i>
                      </div>
                      <input
                        type="number"
                        id="captcha"
                        name="captcha"
                        required
                        value={captcha.answer}
                        onChange={handleCaptchaChange}
                        placeholder="?"
                        className={`w-20 px-3 py-3 border rounded-lg focus:ring-2 transition-all duration-300 text-sm font-inter text-center ${
                          captcha.answer && captcha.isValid 
                            ? 'border-green-300 bg-green-50 focus:ring-green-500 focus:border-green-500' 
                            : captcha.answer && !captcha.isValid
                            ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500'
                            : 'border-blue-200 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                      />
                      {captcha.answer && (
                        <div className="flex items-center">
                          {captcha.isValid ? (
                            <i className="ri-check-line text-green-600 text-lg"></i>
                          ) : (
                            <i className="ri-close-line text-red-600 text-lg"></i>
                          )}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={generateNewCaptcha}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg transition-colors duration-300 cursor-pointer"
                        title={t('contact.form.captcha.refresh')}
                      >
                        <i className="ri-refresh-line text-lg"></i>
                      </button>
                    </div>
                    <p className="text-xs text-blue-600 mt-2 font-inter">
                      {t('contact.form.captcha.instruction')}
                    </p>
                  </div>
                </div>

                <div className="mt-6">
                  <Button 
                    type="submit" 
                    size="lg" 
                    disabled={isSubmitting || !captcha.isValid}
                    className="w-full shadow-colored"
                  >
                    {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
                  </Button>

                  {submitStatus === 'success' && (
                    <div className="text-green-600 text-center font-inter bg-green-50 p-4 rounded-lg border border-green-200 mt-4">
                      {t('contact.form.success')}
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="text-red-600 text-center font-inter bg-red-50 p-4 rounded-lg border border-red-200 mt-4">
                      {!captcha.isValid && captcha.answer ? t('contact.form.captcha.error') : t('contact.form.error')}
                    </div>
                  )}
                </div>
              </form>
            </Card>
          </div>

          {/* Contact Information - Takes up 1 column and same height */}
          <div className="w-full">
            <div className="h-full flex flex-col space-y-6 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              {/* Contact Info Card */}
              <Card className="flex-1 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white shadow-colored hover:shadow-2xl">
                <div className="h-full flex flex-col">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <i className="ri-customer-service-2-line text-2xl text-white"></i>
                    </div>
                    <h3 className="text-2xl font-bold font-poppins">
                      {t('contact.info.title')}
                    </h3>
                    <p className="text-blue-100 mt-2 font-inter">
                      Estamos aquí para ayudarte
                    </p>
                  </div>
                  
                  <div className="flex-1 space-y-6">
                    {contactInfo.map((info) => (
                      <div key={info.label} className="flex items-start space-x-4 p-4 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-all duration-300">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
                          <i className={`${info.icon} text-xl text-white`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-blue-100 text-sm font-inter mb-1 font-semibold">{info.label}</p>
                          {info.href ? (
                            <a 
                              href={info.href} 
                              className="text-white font-bold hover:text-blue-200 transition-colors duration-300 font-inter text-base leading-tight break-words"
                            >
                              {info.value}
                            </a>
                          ) : (
                            <p className="text-white font-bold font-inter text-base leading-tight">{info.value}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

              {/* Social Media Card */}
              <Card className="bg-gradient-to-br from-white via-blue-50 to-blue-100 border-blue-200 hover:shadow-2xl">
                <div className="text-center">
                  <div className="w-14 h-14 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <i className="ri-share-line text-xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-blue-900 mb-2 font-poppins">
                    {t('contact.followUs')}
                  </h3>
                  <p className="text-blue-600 text-sm font-inter mb-6">
                    Conecta con nosotros en redes sociales
                  </p>
                  <div className="flex justify-center space-x-4">
                    {SOCIAL_LINKS.map((social) => (
                      <a
                        key={social.name}
                        href={social.href}
                        className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full flex items-center justify-center hover:from-blue-700 hover:to-blue-800 transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl hover:scale-110 group"
                        aria-label={social.name}
                      >
                        <i className={`${social.icon} text-lg group-hover:scale-110 transition-transform duration-300`}></i>
                      </a>
                    ))}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
}
